import '@widesk-ui/components/styles/List.scss';
import '@widesk-ui/styles/List.scss';

import React               from 'react';
import { ListItemProps }   from '@widesk-ui/components/List/ListItem';
import { ListItem }        from '@widesk-ui/components/List/ListItem';
import ListContext         from '@widesk-ui/contexts/ListContext';
import AntdList            from 'antd/lib/list';
import { observer }        from 'mobx-react-lite';
import ListStore           from '@widesk-ui/stores/ListStore';
import useSplitView        from '@widesk-ui/hooks/useSplitView';
import useDashboard        from '@widesk-ui/hooks/useDashboard';
import useDidMount         from '@widesk-core/hooks/useDidMount';
import ContentError        from '@widesk-ui/components/ContentError';
import useTheme            from '@widesk-ui/hooks/useTheme';
import Pagination          from 'antd/lib/pagination';
import View                from '@widesk-ui/components/View';
import Checkbox            from 'antd/lib/checkbox/Checkbox';
import Observer            from '@widesk-ui/components/Observer';
import { PaginationProps } from '@widesk-ui/Pagination';
import PagedCollection     from '@widesk-core/models/PagedCollection';
import useDrawer           from '@widesk-ui/hooks/useDrawer';

type ListGridType = {
	gutter?: number;
	column?: number;
	xs?: number;
	sm?: number;
	md?: number;
	lg?: number;
	xl?: number;
	xxl?: number;
}

export type ListProps<D extends any[]> = {
	id?: string;
	itemProps: (datum: D[number], listStore: ListStore) => ListItemProps;
	linksClickPrevent?: boolean;
	rowKey?: (datum: D[number]) => string;
	onClickItem?: (datum: D[number], listStore: ListStore, e: MouseEvent, idx: number) => void;
	onAuxClick?: (datum: D[number], listStore: ListStore, e: MouseEvent, idx: number) => void;
	onSelect?: (datum: D[number]) => void;
	onFetchStart?: () => Promise<void | boolean>; // Se déclenche juste avant le fetch. Si la méthode retourne false, le fetch n'est pas déclenché
	onFetchSuccess?: () => void;
	onDidMount?: (store: ListStore) => void;
	pagination?: (PaginationProps & { pagedCollection?: PagedCollection<any> }) | false;
	storageKey?: string;
	urlStorageEnabled?: boolean; // Détermine si les filtres sont stockés dans l'URL
	clickable?: boolean;
	defaultFilters?: Record<string, unknown>;
	grid?: ListGridType;
	checkbox?: boolean;
	keyboardNavigationDisabled?: boolean;
	RowComponent?: React.FunctionComponent<{ children: React.ReactNode; id: string; }>;
	renderRowLeft?: (datum: D[number]) => React.ReactNode;
	listStore?: ListStore;
	dragging?: boolean;
	manualActiveIndex?: boolean;
	footer?: React.ReactNode;
	/** Remplace le "Aucune donnée" par défaut quand la liste est vide — permet par exemple d'y mettre
	 * une invitation à créer un premier élément. Omis : état vide par défaut d'Antd, inchangé. */
	emptyText?: React.ReactNode;
} & ({
	dataSource: D;
	fetch?: undefined;
} | {
	dataSource?: undefined;
	fetch: (page: number, step: number, filters: Record<string, unknown>) => Promise<D>;
})

export const List = observer(<D extends any[]>(props: ListProps<D>) => {
	const { manualActiveIndex, onAuxClick, itemProps, pagination, RowComponent, rowKey, renderRowLeft } = props;

	const { splitViewStore } = useSplitView();
	const { dashboardStore } = useDashboard();
	const theme = useTheme();
	const drawerContext = useDrawer();

	const listStore = React.useMemo(() => props.listStore || new ListStore(props), []);

	listStore.setProps(props);

	useDidMount(() => dashboardStore?.pushListStore(listStore));
	useDidMount(() => listStore?.setSplitViewStore(splitViewStore));

	useDidMount(async () => {
		if (props.onFetchStart) {
			if (await props.onFetchStart() === false) { // Si la méthode retourne false, le fetch n'est pas exécuté
				return false;
			}
		}

		await listStore.fetch(listStore.page, listStore.pageSize, listStore.filters);

		if (props.onFetchSuccess) {
			props.onFetchSuccess();
		}
	});

	useDidMount(() => {
		if (props.onDidMount) {
			props.onDidMount(listStore);
		}
	});

	React.useEffect(() => {
		const handleKeyDown = async (event: KeyboardEvent) => {
			if (props.keyboardNavigationDisabled) return;

			if (ref.current?.contains(document.activeElement)) {
				switch (event.key) {
					case 'ArrowUp':
						return listStore.prev();
					case 'ArrowDown':
						return listStore.next();
					case 'ArrowLeft':
						return listStore.prevPage();
					case 'ArrowRight':
						return listStore.nextPage();
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [props.keyboardNavigationDisabled]);

	const ref = React.useRef<HTMLDivElement | null>(null);
	React.useEffect(() => {
		if (props.linksClickPrevent) {
			const listener = (event: MouseEvent) => {
				if ((event.target as any)?.href && !event.ctrlKey) {
					event.preventDefault();
				}
			};
			ref.current?.addEventListener('click', listener);
			return () => {
				ref.current?.removeEventListener('click', listener);
			};
		}
	}, []);

	const ListPagination = pagination !== false ? observer((props: PaginationProps) => (
		<Pagination
			responsive
			onChange={async (page, pageSize) => {
				const pageV = listStore.pageSize.toString() !== pageSize.toString() ? 1 : page; // redirect sur la 1 page

				/* debounds */
				await listStore.fetch(pageV, pageSize, {
					...listStore.filters,
					idx: 0,
				});
			}}
			showPrevNextJumpers={(theme.Pagination as any)?.showPrevNextJumpers || false}
			showTotal={(total, range) => (total === range[1] && range[0] === 1) ? (
				<span>{total} résultats</span>
			) : (
				<View row gapH={1}>
					<span>{range[0]}</span>
					<span>-</span>
					<span>{range[1]}</span>
					{total && (<span>/</span>)}
					{total && (<span>{total}</span>)}
				</View>
			)}
			defaultPageSize={listStore.pageSize || 10}
			total={(pagination && pagination.pagedCollection) ? pagination.pagedCollection.total : listStore.data.length}
			current={(pagination && pagination.pagedCollection) && pagination.pagedCollection.page || 0}
			showSizeChanger
			showLessItems
			hideOnSinglePage={pagination && pagination.hideOnSinglePage ? pagination.hideOnSinglePage : true}
			size="small"
			className={'widesk-list-ant-pagination'}

			{...pagination}
			{...props}
		/>
	)) : undefined;
	// Une seule page : rien à paginer, et poser quand même le pied laisserait un bandeau vide (le
	// conteneur du Drawer se dessine avec sa bordure même quand son contenu ne rend rien).
	const hasSinglePage = (pagination || undefined)?.pagedCollection?.hasSinglePage ?? false;

	React.useEffect(() => {
		if (hasSinglePage) return;

		if (drawerContext && drawerContext.setFooter !== undefined && !drawerContext.footer && ListPagination) {
			drawerContext.setFooter(<ListPagination style={{ padding: 0 }}/>);
		}
	});

	// Doit être placé après tous les hooks, sinon lève une exception « Rendered fewer hooks than expected »
	if (listStore.error) {
		return <ContentError error={listStore.error} retry={() => listStore.reload()} />;
	}

	const isLoading = listStore.isLoading;

	return (
		<ListContext.Provider value={{ listStore }}>
			<AntdList
				key={rowKey && `${listStore.data.map(d => rowKey(d)).join('.')}+${listStore.total}`}
				id={props.id}
				className={`widesk-list ${props.grid ? 'widesk-list-grid' : ''} ${props.dragging ? 'widesk-list-dragging' : ''}`}
				grid={props.grid ? {
					gutter: props.grid.gutter || theme.marginMD,
					...props.grid,
				} : undefined}
				rowKey={props.rowKey}
				loading={isLoading}
				footer={props.footer}
				dataSource={listStore.data}
				locale={props.emptyText !== undefined ? { emptyText: props.emptyText } : undefined}
				renderItem={(datum, idx) => {
					const content = (
						<Observer render={() => {
							return (
								<ListItem
									active={props.clickable && (
										(!manualActiveIndex && listStore.currentIndex === idx)
										|| (manualActiveIndex && listStore.manualCurrentIndex === idx)
									)}
									last={idx + 1 === listStore.data.length}
									onClick={props.clickable ? e => {
										listStore.setCurrentIndex(idx, true);

										if (props.onClickItem) {
											props.onClickItem(datum, listStore, e as never, idx);
										}

										return false;
									} : undefined}
									onAuxClick={e => {
										if (onAuxClick) {
											onAuxClick(datum, listStore, e as never, idx);
										}
									}}

									{...itemProps(datum, listStore)}

									renderLeft={() => {
										const itemRenderLeft = itemProps(datum, listStore).renderLeft;

										return (
											props.checkbox
											|| (!!itemRenderLeft)
											|| renderRowLeft
										) && (
											<Observer render={() => {
												return (
													<View gap="xs" row>
														{props.checkbox && (
															<Checkbox
																onClick={(e) => e.stopPropagation()}
																checked={listStore.checkboxListIndex.includes(idx)}
																onChange={() => listStore.toggleCheckbox(idx)}
															/>
														)}

														{props.renderRowLeft?.(datum)}

														{(!!itemRenderLeft) && itemRenderLeft?.()}
													</View>
												);
											}} />
										);
									}}
								/>
							);
						}} />
					);

					if (RowComponent) {
						return (
							<RowComponent id={props.rowKey?.(datum).toString() || ''}>
								{content}
							</RowComponent>
						);
					}

					return content;
				}}
			/>

			{/* `hasSinglePage` ici aussi : sans ça, une pagination écartée du pied du tiroir se contentait
			    de réapparaître dans le corps de la liste. */}
			{props.pagination !== false && !hasSinglePage && !drawerContext?.footer && ListPagination && <ListPagination />}
		</ListContext.Provider>
	);
});